import { useEffect, useState } from 'react';
import api from '../../utils/api';
import OrganicLoader from '../organic/OrganicLoader.jsx';
import PhylogenyTree, { matchPaths } from '../PhylogenyTree';
import * as S from '../specimens/styles';

/*
 * The reference phylogeny (GET /phylogeny/reference) opened on the isolate's
 * genus, under its DNA sequences: in the row's DNA tab and in the DNA stage
 * opened in place (IsolatePeek). It is the literature baseline the bank's
 * sequences will be placed against, not a tree built from this isolate's
 * sequence, and it says so.
 *
 * The genus is the sequence's when an analysis named one and the tree has
 * it, else the record's. When the tree has both and they differ, both are
 * offered and the tree opens on the one chosen. A genus the tree does not
 * hold yet (the tree is built from the reference bank's genera,
 * backend/phylogeny/opentree.py) is named as missing, never "highlighted".
 */

/** First words that name no genus (routes/isolate_pipeline.py NOT_A_GENUS). */
const NOT_A_GENUS = new Set(['unknown', 'unidentified', 'unresolved', 'sp', 'spp', 'cf', 'aff', 'fungus', 'fungi']);

/**
 * The genus a Latin name starts with, capitalised, or '' for anything else:
 * "Unresolved", "sp. 3", an abbreviation ("G. vellereum"), a Hebrew name.
 * isolate_pipeline.genus_of's rule, so the tree highlights a genus exactly
 * when the pipeline would compare one.
 */
export function genusOf(name) {
  const words = String(name || '').trim().split(/\s+/);
  if (!words[0] || words[0].endsWith('.')) return '';
  const word = words[0].replace(/^[,;:()[\]]+|[,;:()[\]]+$/g, '').toLowerCase();
  if (word.length < 2 || !/^[a-z]+$/.test(word) || NOT_A_GENUS.has(word)) return '';
  return word[0].toUpperCase() + word.slice(1);
}

/** The genus the sequences name (the newest current analysis that names
 *  one) and the record's, each '' when there is none. */
/** Whether the reference tree holds this genus (its search would mark it). */
export function inTree(reference, genus) {
  return !!genus && !!reference?.tree && matchPaths(reference.tree, genus).matches.size > 0;
}

export function generaOf(dossier, item) {
  const answers = dossier?.identifications?.dna || [];
  const sequence = answers.map((a) => genusOf(a.recommended_identification)).find(Boolean) || '';
  const record = genusOf(item?.taxon_name) || genusOf(dossier?.taxonomy?.scientific_name);
  return { sequence, record };
}

// One reference tree per page: every row's tab and the stage dialog share it.
// A failed read is not kept, so the next tab opened asks again.
let request = null;
function loadReference() {
  if (!request) {
    request = api.get('/phylogeny/reference').then((res) => res?.data || null);
    request.catch(() => { request = null; });
  }
  return request;
}
/** Tests: forget the tree read, as a new page would. */
export function forgetReference() { request = null; }

export default function IsolatePhylogeny({ dossier, item, t }) {
  const { sequence, record } = generaOf(dossier, item);
  const [chosen, setChosen] = useState(null);
  const [state, setState] = useState({ status: 'loading', data: null });
  const ready = state.status === 'ready';
  const reference = state.data?.reference;
  const candidates = [...new Set([sequence, record].filter(Boolean))];
  const known = ready ? candidates.filter((g) => inTree(reference, g)) : candidates;
  const missing = ready ? candidates.filter((g) => !known.includes(g)) : [];
  const genus = chosen || known[0] || '';

  useEffect(() => {
    let alive = true;
    loadReference()
      .then((data) => { if (alive) setState({ status: data?.reference ? 'ready' : 'failed', data }); })
      .catch((e) => { if (alive) setState({ status: e?.response?.status === 404 ? 'missing' : 'failed', data: null }); });
    return () => { alive = false; };
  }, []);

  const offer = known.length > 1;
  return (
    <section data-testid="hub-dna-tree" data-genus={genus.toLowerCase()} style={{ marginTop: 14 }}>
      <h4 style={{ fontSize: 14, margin: '0 0 4px' }}>
        {genus ? t('extracts.tree.title', { genus }) : t('extracts.tree.titleNoGenus')}
      </h4>
      <p style={{ ...S.hint, margin: '0 0 6px' }}>{t('extracts.tree.baseline')}</p>
      {missing.length > 0 && (
        <p role="note" style={{ ...S.hint, margin: '0 0 6px' }} data-testid="hub-dna-tree-missing">
          {t('extracts.tree.notInTree', { genera: missing.join(', ') })}
        </p>
      )}
      {offer && (
        <div role="group" aria-label={t('extracts.tree.which')} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
          {[['sequence', sequence], ['record', record]].map(([source, name]) => (
            <button key={source} type="button" aria-pressed={genus === name} onClick={() => setChosen(name)}
              title={t(`extracts.tree.tip.${source}`)} data-testid={`hub-dna-tree-${source}`}
              style={{ ...S.ghostBtn, padding: '3px 10px', fontSize: 12, fontWeight: genus === name ? 700 : 400 }}>
              {t(`extracts.tree.${source}`)}: <bdi dir="ltr" style={{ fontStyle: 'italic' }}>{name}</bdi>
            </button>
          ))}
        </div>
      )}
      {state.status === 'loading' && <OrganicLoader block label={t('common:loading')} slowLabel={t('common:stillWorking')} />}
      {state.status === 'missing' && <p style={S.hint}>{t('extracts.tree.notBuilt')}</p>}
      {state.status === 'failed' && <p style={S.hint}>{t('extracts.tree.failed')}</p>}
      {ready && (
        // Keyed by the genus: the tree's search starts from it, so choosing the
        // other genus opens the tree again on that one.
        <PhylogenyTree key={genus} reference={reference} heldGenera={state.data.held_genera || {}}
          initialQuery={genus} />
      )}
    </section>
  );
}
